/**
 * GiornataSelector — dropdown delle giornate fanta di una stagione.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuthStore } from "../../lib/auth-store";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

interface GiornataSelectorProps {
  stagioneId: string | null;
  value: string;
  onChange: (id: string) => void;
  /** Se true auto-seleziona la prima giornata 'da_giocare' o l'ultima. Default false */
  autoSelectFirstAperta?: boolean;
  label?: string;
  className?: string;
}

export function GiornataSelector({
  stagioneId,
  value,
  onChange,
  autoSelectFirstAperta = false,
  label = tr("Giornata"),
  className = "",
}: GiornataSelectorProps) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: giornate, isLoading } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });

  useEffect(() => {
    if (!autoSelectFirstAperta || value || !giornate?.length) return;
    const aperta = giornate.find((g) => g.stato === "da_giocare") ?? giornate[giornate.length - 1];
    if (aperta) onChange(aperta.id);
  }, [giornate, value, autoSelectFirstAperta, onChange]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!stagioneId || isLoading || !giornate?.length}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      >
        {!stagioneId && <option>{tr("— seleziona stagione prima —")}</option>}
        {stagioneId && isLoading && <option>{tr("Caricamento…")}</option>}
        {stagioneId && !isLoading && !giornate?.length && (
          <option value="">{tr("— nessuna giornata —")}</option>
        )}
        {stagioneId && !isLoading && giornate?.length && (
          <>
            <option value="">{tr("— seleziona —")}</option>
            {giornate.map((g) => (
              <option key={g.id} value={g.id}>
                G{g.numero} (SA {g.numero_serie_a}) · {etichetta(g.stato)}
              </option>
            ))}
          </>
        )}
      </select>
    </div>
  );
}
